import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Search, Trash2, Plus, Pencil, MessageCircle, Info, X, UserPlus, SearchX, Sparkles, ArrowLeft, ClipboardList } from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackChip,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  Toolbar,
  SearchControl,
  ClearButton,
  EntriesControl,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  type MenuCoords,
} from "./quotationMasterListShared";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import { SubmitButton } from "../../molecules/Dashboard/Member/memberFormStyles";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../atoms/Utlis/getApiErrorMessage";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { useAppSelector } from "../../../Redux/Hooks";
import { usePermissions } from "../../../hooks/usePermissions";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { ListReportPrintTemplate, ReportStatusBadge, CellBold } from "./ListReportPrintTemplate";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import { getEmployeesAPI } from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import {
  getOrderWorkAssignmentsAPI,
  createOrderWorkAssignmentAPI,
  deleteOrderWorkAssignmentAPI,
  updateOrderWorkAssignmentAPI,
  notifyWorkAssignmentWhatsAppAPI,
  notifyTeamMemberAPI,
  notifyTeamAPI,
} from "../../../Redux/ApiCalls/Dashboard/OrderAPI";

import { OrderWorkRecord } from "./OrderWork/types";
import {
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import AssignWorkModal from "./OrderWork/AssignWorkModal";
import NotifyMemberModal from "./OrderWork/NotifyMemberModal";

const WorkPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const WorkPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const WorkTableScroll = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

// ─── Main Page ─────────────────────────────────────────────────────────────

const AllOrderWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectedBranch } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission("orders.update");
  const canDelete = hasPermission("orders.delete");
  const hasAnyRowAction = canUpdate || canDelete;
  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [records, setRecords] = useState<OrderWorkRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [dbOrders, setDbOrders] = useState<any[]>([]);
  const [dbEmployees, setDbEmployees] = useState<any[]>([]);

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const [entries, setEntries] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<OrderWorkRecord | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [notifyMemberRecord, setNotifyMemberRecord] = useState<OrderWorkRecord | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [alreadyAssignedOrder, setAlreadyAssignedOrder] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [notifyConfirmId, setNotifyConfirmId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Extract query parameters (for auto-filling order reference)
  const queryParams = new URLSearchParams(window.location.search);
  const urlOrderNo = queryParams.get("orderNo") || "";

  const loadDependencies = async () => {
    setLoading(true);
    try {
      const fetchAllPaginated = async (fetchFn: (p: any) => Promise<any>, extra: any = {}): Promise<any[]> => {
        let all: any[] = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await fetchFn({ page, page_size: 100, ...extra });
          const list = res?.results || (Array.isArray(res) ? res : []);
          all = [...all, ...list];
          if (res?.next && list.length === 100) {
            page += 1;
          } else {
            hasMore = false;
          }
        }
        return all;
      };

      const fetchOrdersPaginated = async (): Promise<any[]> => {
        let all: any[] = [];
        let page = 1;
        let hasMore = true;
        const branchQuery = selectedBranch?.id ? `&branch_id=${selectedBranch.id}` : '';
        while (hasMore) {
          const res = await apiCall({ method: "GET", url: `/order/orders/?page=${page}&page_size=100${branchQuery}` });
          const list = res?.results || (Array.isArray(res) ? res : []);
          all = [...all, ...list];
          if (res?.next && list.length === 100) {
            page += 1;
          } else {
            hasMore = false;
          }
        }
        return all;
      };

      const [ordersList, employeesList] = await Promise.all([
        fetchOrdersPaginated(),
        fetchAllPaginated(getEmployeesAPI, { branch_id: selectedBranch?.id }),
      ]);
      setDbOrders(ordersList);
      setDbEmployees(employeesList);
    } catch {
      showErrorToast("Failed to load dependency data");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAssignments = async () => {
    setLoading(true);
    try {
      const res = await getOrderWorkAssignmentsAPI({
        page: currentPage,
        page_size: entries,
        search: searchText,
        branch_id: selectedBranch?.id,
      });
      const results = res?.results || res || [];
      setTotalCount(res?.count || (res?.results ? res.results.length : res?.length) || 0);
      const normalized = results.map((r: any) => {
        let orderNoStr = "N/A";
        if (r.order_no) {
          orderNoStr = r.order_no;
        } else if (r.order) {
          const match = dbOrders.find((o: any) => o.id === r.order);
          if (match) orderNoStr = match.order_no;
        }

        const assignedMembersList = (r.assigned_members || []).map((m: any) => {
          const emp = m.team_member !== undefined ? m.team_member : m.employee;
          return {
            employee: typeof emp === "object" ? emp.id : emp,
            role: m.role || "Staff",
            amount: String(m.amount || 0)
          };
        });

        let teamMembersIds: number[] = [];
        if (assignedMembersList.length > 0) {
          teamMembersIds = assignedMembersList.map((m: any) => m.employee);
        } else if (r.team_members && Array.isArray(r.team_members)) {
          teamMembersIds = r.team_members.map((tm: any) => typeof tm === "object" ? tm.id : tm);
        } else if (r.employee_name) {
          const names = r.employee_name.split(",").map((n: string) => n.trim().toLowerCase());
          teamMembersIds = dbEmployees
            .filter((emp: any) => names.includes(emp.name.toLowerCase()))
            .map((emp: any) => emp.id);
        }

        return {
          id: String(r.id),
          orderNo: orderNoStr,
          orderId: r.order,
          orderEvent: r.order_event,
          employeeName: r.employee_name,
          eventDate: r.event_date,
          eventSession: r.event_session,
          eventType: r.event_type,
          role: r.role,
          amount: String(r.amount),
          status: r.status,
          teamMembers: teamMembersIds,
          assignedMembers: assignedMembersList,
        };
      });
      setRecords(normalized);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to load work assignments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDependencies();
  }, [selectedBranch?.id]);

  // Auto-open modal when navigated from AllOrders with ?orderNo=
  useEffect(() => {
    if (!urlOrderNo || dbOrders.length === 0) return;
    setEditRecord(null);
    setModalOpen(true);
  }, [dbOrders]); // fires once after loadDependencies sets dbOrders

  useEffect(() => {
    if (dbOrders.length > 0) {
      fetchWorkAssignments();
    }
  }, [dbOrders, currentPage, entries, searchText, selectedBranch?.id]);

  const handleNotifyWhatsApp = async (id: string) => {
    const r = records.find(rec => rec.id === id);
    if (!r) return;

    const assignedCount = r.assignedMembers ? r.assignedMembers.length : (r.teamMembers ? r.teamMembers.length : 0);
    if (assignedCount === 1) {
      const memberId = r.assignedMembers?.[0]?.employee ?? r.teamMembers?.[0];
      if (!memberId) return;
      setNotifyingId(id);
      try {
        await notifyWorkAssignmentWhatsAppAPI(id, { team_member_id: memberId });
        showSuccessToast("WhatsApp reminder sent successfully");
      } catch (err: any) {
        showErrorToast(err?.message || "Failed to send WhatsApp reminder");
      } finally {
        setNotifyingId(null);
      }
    } else if (assignedCount > 1) {
      setNotifyMemberRecord(r);
    } else {
      setNotifyingId(id);
      try {
        await notifyWorkAssignmentWhatsAppAPI(id);
        showSuccessToast("WhatsApp reminder sent successfully");
      } catch (err: any) {
        showErrorToast(err?.message || "Failed to send WhatsApp reminder");
      } finally {
        setNotifyingId(null);
      }
    }
  };

  const handleModalNotify = async (selectedIds: number[]) => {
    if (!notifyMemberRecord) return;
    const assignmentId = notifyMemberRecord.id;
    setNotifyMemberRecord(null);
    setNotifyingId(assignmentId);
    try {
      if (selectedIds.length === 1) {
        await notifyWorkAssignmentWhatsAppAPI(assignmentId, { team_member_id: selectedIds[0] });
      } else {
        await Promise.all(selectedIds.map(empId =>
          notifyWorkAssignmentWhatsAppAPI(assignmentId, { team_member_id: empId })
        ));
      }
      showSuccessToast("WhatsApp reminder sent to selected member(s) successfully");
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to send WhatsApp reminder");
    } finally {
      setNotifyingId(null);
    }
  };

  const handleSave = async (payload: any) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (editRecord) {
        await updateOrderWorkAssignmentAPI(editRecord.id, payload);
        if (payload.notify_whatsapp) {
          try {
            await notifyWorkAssignmentWhatsAppAPI(editRecord.id);
          } catch (e) { }
        }
        showSuccessToast("Work assignment updated successfully");
        fetchWorkAssignments();
      } else {
        if (Array.isArray(payload)) {
          const results = await Promise.all(payload.map(item => createOrderWorkAssignmentAPI(item)));
          if (payload[0]?.notify_whatsapp) {
            await Promise.all(results.map(res => res?.id ? notifyWorkAssignmentWhatsAppAPI(res.id) : Promise.resolve()));
          }
        } else {
          const res = await createOrderWorkAssignmentAPI(payload);
          if (payload.notify_whatsapp && res?.id) {
            try {
              await notifyWorkAssignmentWhatsAppAPI(res.id);
            } catch (e) { }
          }
        }
        showSuccessToast("Work assignment created successfully");
        await loadDependencies(); // refresh has_work_assignment flags
      }
      setModalOpen(false);
      setEditRecord(null);
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save work assignment"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (record: OrderWorkRecord) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null) return;
    try {
      await deleteOrderWorkAssignmentAPI(deleteConfirmId);
      showSuccessToast("Work assignment deleted");
      fetchWorkAssignments();
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to delete assignment"));
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    copyDataToClipboard(
      records,
      ["Order No", "Employee", "Event Date", "Session", "Role", "Amount", "Status"],
      ["orderNo", "employeeName", "eventDate", "eventSession", "role", "amount", "status"],
      "Work assignments copied to clipboard"
    );
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToCSV(
      records,
      ["Order No", "Employee", "Event Date", "Session", "Event Type", "Role", "Amount", "Status"],
      ["orderNo", "employeeName", "eventDate", "eventSession", "eventType", "role", "amount", "status"],
      "order_work_export.csv",
      "CSV exported successfully"
    );
  };

  const handleExportExcel = (e: React.MouseEvent) => {
    e.preventDefault();
    exportDataToExcel(
      records,
      ["Order No", "Employee", "Event Date", "Session", "Event Type", "Role", "Amount", "Status"],
      ["orderNo", "employeeName", "eventDate", "eventSession", "eventType", "role", "amount", "status"],
      "order_work_export.xls",
      "Excel exported successfully"
    );
  };

  const workReportRows = useMemo(
    () =>
      records.map((r) => {
        const amountRaw =
          r.assignedMembers && r.assignedMembers.length > 0
            ? r.assignedMembers.reduce((sum, m) => sum + (Number(m.amount) || 0), 0)
            : Number(r.amount) || 0;

        const roleLabel =
          r.assignedMembers && r.assignedMembers.length > 0
            ? r.assignedMembers
                .map((m) => {
                  const empName = dbEmployees.find((e) => e.id === m.employee)?.name || "Staff";
                  return `${empName}: ${m.role}`;
                })
                .join(", ")
            : r.role;

        return {
          orderNo: r.orderNo,
          employeeName: r.employeeName,
          eventDate: r.eventDate || "—",
          eventSession: r.eventSession || "—",
          eventType: r.eventType || "—",
          role: roleLabel || "—",
          amount: amountRaw > 0 ? `₹${formatINR(amountRaw)}` : "—",
          amount_raw: amountRaw,
          status: r.status,
          status_key: r.status,
        };
      }),
    [records, dbEmployees]
  );

  const workReportSummary = useMemo(() => {
    const totalAmount = workReportRows.reduce((sum, row) => sum + row.amount_raw, 0);
    return [
      { label: "Total Assignments", value: String(workReportRows.length) },
      { label: "Total Assigned Amount", value: `₹${formatINR(totalAmount)}`, highlight: true },
    ];
  }, [workReportRows]);

  const getReportStatusStyle = (status: string) =>
    statusColors[status] || { bg: "rgba(93,28,52,0.1)", color: "#5D1C34" };

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "order_work_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visible = records;
  const totalPages = Math.ceil(totalCount / entries) || 1;
  const hasActiveSearch = !!searchText;

  const closeMenu = useCallback(() => {
    setActiveMenuId(null);
    setMenuCoords(null);
  }, []);

  const handleToggleMenu = useCallback(
    (id: string) => {
      if (activeMenuId === id) {
        closeMenu();
        return;
      }

      const btn = menuButtonRefs.current[id];
      if (!btn) {
        setActiveMenuId(id);
        return;
      }

      const btnRect = btn.getBoundingClientRect();
      const estimatedMenuHeight = 160;
      const gap = 8;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;
      const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const right = Math.max(8, window.innerWidth - btnRect.right);

      setMenuCoords(
        openUp
          ? { bottom: window.innerHeight - btnRect.top + gap, right }
          : { top: btnRect.bottom + gap, right }
      );
      setActiveMenuId(id);
    },
    [activeMenuId, closeMenu]
  );

  useEffect(() => {
    if (activeMenuId === null) return;
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId, closeMenu]);

  const activeMenuRecord = useMemo(
    () => visible.find((r) => r.id === activeMenuId) ?? null,
    [visible, activeMenuId]
  );

  return (
    <WorkPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackChip type="button" onClick={() => navigate("/dashboard/orders/all")} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackChip>
            <HeroText>
              <HeroBadge><ClipboardList size={11} /> Order management</HeroBadge>
              <PageTitle className="linear-text">Order Work Assignments</PageTitle>
              <PageSubtitle>Track and manage employee work assignments across all orders.</PageSubtitle>
            </HeroText>
          </HeroLeft>
          <PermissionGate permission="orders.update">
            <SubmitButton
              type="button"
              $ready
              onClick={() => { setEditRecord(null); setModalOpen(true); }}
              style={{ minWidth: 180, height: 46 }}
            >
              <Plus size={16} /> Assign Work
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <WorkPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                placeholder="Search assignments"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select value={entries} onChange={(e) => { setEntries(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>

        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Work assignment records</ListTitle>
          {!loading && (
            <CountBadge>
              {totalCount} record{totalCount !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : visible.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveSearch ? <SearchX size={36} strokeWidth={1.5} /> : <UserPlus size={36} strokeWidth={1.5} />}
            title={hasActiveSearch ? "No assignments found" : "No work assignments yet"}
            description={
              hasActiveSearch
                ? "Nothing matched your search. Try a different order number, employee name, or role."
                : "Assign your first team member to an order to track roles, amounts, and WhatsApp notifications."
            }
            actions={[
              ...(hasActiveSearch
                ? [{
                    label: "Clear search",
                    onClick: () => { setSearchVal(""); setSearchText(""); setCurrentPage(1); },
                    variant: "secondary" as const,
                  }]
                : []),
              ...(canUpdate
                ? [{
                    label: hasActiveSearch ? "Assign Work" : "Assign Your First Work",
                    onClick: () => { setEditRecord(null); setModalOpen(true); },
                    icon: <Plus size={15} />,
                  }]
                : []),
            ]}
            hint={!hasActiveSearch ? {
              icon: <Sparkles size={13} />,
              text: "Assign roles, track status & notify via WhatsApp",
            } : undefined}
          />
        ) : (
          <WorkTableScroll ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={900}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}><span>ID</span></th>
                  <th><span>Order No</span></th>
                  <th><span>Employee Name</span></th>
                  <th><span>Event Info</span></th>
                  <th><span>Role</span></th>
                  <th><span>Amount (₹)</span></th>
                  <th><span>Status</span></th>
                  {hasAnyRowAction && <th style={{ width: 120, textAlign: "right" }}><span>Action</span></th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, idx) => (
                  <tr key={r.id}>
                    <td style={{ color: "rgba(23,23,23,0.42)", fontWeight: 600 }}>
                      {(currentPage - 1) * entries + idx + 1}
                    </td>
                    <td><OrderNo>{r.orderNo}</OrderNo></td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.employeeName}</td>
                    <td>
                      <EventInfoCell>
                        <EventDate>{r.eventDate}</EventDate>
                        <EventMeta>{r.eventSession} · {r.eventType}</EventMeta>
                      </EventInfoCell>
                    </td>
                    <td>
                      {r.assignedMembers && r.assignedMembers.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {r.assignedMembers.map((m: any, idx: number) => {
                            const empName = dbEmployees.find(e => e.id === m.employee)?.name || "Staff";
                            return (
                              <div key={idx} style={{ fontSize: "0.85em", display: "flex", alignItems: "center", gap: "4px" }}>
                                <span style={{ fontWeight: 500, color: "rgba(23,23,23,0.7)" }}>{empName}:</span>
                                <RoleBadge style={{ display: "inline-block", margin: 0, padding: "2px 6px", fontSize: "0.9em" }}>{m.role}</RoleBadge>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <RoleBadge>{r.role}</RoleBadge>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {r.assignedMembers && r.assignedMembers.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {r.assignedMembers.map((m: any, idx: number) => {
                            const empName = dbEmployees.find(e => e.id === m.employee)?.name || "Staff";
                            const hasAmount = m.amount && Number(m.amount) !== 0;
                            return (
                              <div key={idx} style={{ fontSize: "0.85em" }}>
                                <span style={{ color: "rgba(23,23,23,0.6)", fontWeight: 400 }}>{empName}:</span> {hasAmount ? `₹${m.amount}` : "-"}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        (r.amount && Number(r.amount) !== 0) ? (
                          <AmountCell>₹{r.amount}</AmountCell>
                        ) : (
                          "-"
                        )
                      )}
                    </td>
                    <td>
                      <StatusBadge $status={r.status}>{r.status}</StatusBadge>
                    </td>
                    {hasAnyRowAction && (
                      <td style={{ textAlign: "right" }}>
                        <MenuContainer>
                          <ThreeDotsButton
                            ref={(el) => { menuButtonRefs.current[r.id] = el; }}
                            type="button"
                            aria-expanded={activeMenuId === r.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMenu(r.id);
                            }}
                          >
                            <Dot />
                            <Dot />
                            <Dot />
                          </ThreeDotsButton>
                        </MenuContainer>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              </PaymentsTable>
            </TableWrap>
          </WorkTableScroll>
        )}

        {!loading && visible.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            Showing {totalCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalCount)} of {totalCount} entries
          </FooterText>
          <Pagination>
            <PageButton disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</PageButton>
            {Array.from({ length: totalPages }).map((_, i) => (
              <PageButton key={i} $active={currentPage === i + 1} onClick={() => setCurrentPage(i + 1)}>{i + 1}</PageButton>
            ))}
            <PageButton disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</PageButton>
          </Pagination>
        </FooterRow>
      </WorkPanel>

      {activeMenuRecord && menuCoords &&
        createPortal(
          <>
            <Backdrop onClick={closeMenu} />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {canUpdate && (
                <MenuItem
                  type="button"
                  disabled={notifyingId !== null}
                  onClick={() => {
                    closeMenu();
                    setNotifyConfirmId(activeMenuRecord.id);
                  }}
                >
                  <ItemIconContainer>
                    <MessageCircle size={14} />
                  </ItemIconContainer>
                  <ItemLabel>{notifyingId === activeMenuRecord.id ? "Sending..." : "Notify WhatsApp"}</ItemLabel>
                </MenuItem>
              )}
              {canUpdate && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    handleEdit(activeMenuRecord);
                  }}
                >
                  <ItemIconContainer>
                    <Pencil size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Edit</ItemLabel>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    closeMenu();
                    setDeleteConfirmId(activeMenuRecord.id);
                  }}
                >
                  <ItemIconContainer>
                    <Trash2 size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Delete</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        )}

      {/* Assign / Edit Modal */}
      {modalOpen && (
        <AssignWorkModal
          record={editRecord}
          orders={dbOrders}
          employees={dbEmployees}
          existingWorkAssignments={records}
          defaultOrderNo={urlOrderNo}
          onSave={handleSave}
          isSaving={isSaving}
          onClose={() => { setModalOpen(false); setEditRecord(null); }}
        />
      )}

      {/* Notify Member Modal */}
      {notifyMemberRecord && (
        <NotifyMemberModal
          record={notifyMemberRecord}
          employees={dbEmployees}
          onClose={() => setNotifyMemberRecord(null)}
          onNotify={handleModalNotify}
        />
      )}

      {/* Already Assigned Info Dialog */}
      {alreadyAssignedOrder !== null && (
        <AlreadyAssignedOverlay onClick={() => setAlreadyAssignedOrder(null)}>
          <AlreadyAssignedBox onClick={(e) => e.stopPropagation()}>
            <AlreadyAssignedIconWrap>
              <Info size={28} color="#ffffff" />
            </AlreadyAssignedIconWrap>
            <AlreadyAssignedTitle>Work Already Assigned</AlreadyAssignedTitle>
            <AlreadyAssignedBody>
              Order <strong>{alreadyAssignedOrder}</strong> already has a work assignment.
              You can edit it directly from the table below.
            </AlreadyAssignedBody>
            <AlreadyAssignedCloseBtn onClick={() => setAlreadyAssignedOrder(null)}>
              Got it
            </AlreadyAssignedCloseBtn>
          </AlreadyAssignedBox>
        </AlreadyAssignedOverlay>
      )}

      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this work assignment?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
        />
      )}

      {notifyConfirmId !== null && (
        <DeletePopup
          text="Send a WhatsApp notification for this work assignment?"
          buttonText="Notify"
          cancel={() => setNotifyConfirmId(null)}
          customIcon={<MessageCircle size={36} color="white" />}
          Delete={() => {
            const id = notifyConfirmId;
            setNotifyConfirmId(null);
            handleNotifyWhatsApp(id);
          }}
        />
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Order Work Assignments"
        subtitle="Employee work assignments overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: "orderNo", label: "Order No", width: "12%" },
          { key: "employeeName", label: "Employee", width: "16%" },
          { key: "eventDate", label: "Event Date", width: "12%" },
          { key: "eventSession", label: "Session", width: "10%" },
          { key: "eventType", label: "Event Type", width: "14%" },
          { key: "role", label: "Role", width: "16%" },
          {
            key: "amount",
            label: "Amount",
            align: "right",
            width: "12%",
            render: (value) => <CellBold>{String(value)}</CellBold>,
          },
          {
            key: "status",
            label: "Status",
            align: "center",
            width: "12%",
            render: (value, row) => {
              const style = getReportStatusStyle(String(row.status_key || ""));
              return (
                <ReportStatusBadge $bg={style.bg} $color={style.color}>
                  {String(value)}
                </ReportStatusBadge>
              );
            },
          },
        ]}
        rows={workReportRows}
        summaryItems={workReportSummary}
      />
    </WorkPage>
  );
};

export default AllOrderWorkPage;

// ─── Styled Components ─────────────────────────────────────────────────────

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 0 22px;
  flex-shrink: 0;
`;

const OrderNo = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const EventInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EventDate = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.88);
  white-space: nowrap;
`;

const EventMeta = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
  white-space: nowrap;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 4px 10px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
`;

const statusColors: Record<string, { bg: string; color: string }> = {
  Assigned: { bg: "rgba(49,166,106,0.12)", color: "#31a66a" },
  "In Progress": { bg: "rgba(53,183,200,0.12)", color: "#35b7c8" },
  Completed: { bg: "rgba(99,102,241,0.12)", color: "#6366f1" },
  Pending: { bg: "rgba(240,154,62,0.15)", color: "#f09a3e" },
};

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 14px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => statusColors[p.$status]?.bg ?? "rgba(23,23,23,0.08)"};
  color:      ${(p) => statusColors[p.$status]?.color ?? "#171717"};
`;

// ─── Already Assigned Dialog ──────────────────────────────────────────────────

const AlreadyAssignedOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 23, 23, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;

const AlreadyAssignedBox = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 32px 28px 28px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 24px 60px rgba(23, 23, 23, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  animation: fadeInScale 0.2s ease;
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.92); }
    to   { opacity: 1; transform: scale(1); }
  }
`;

const AlreadyAssignedIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(144.01deg, #f59e0b 0%, #ef4444 130%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.25);
  margin-bottom: 4px;
`;

const AlreadyAssignedTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
`;

const AlreadyAssignedBody = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.6;

  strong { color: #171717; font-weight: 700; }
`;

const AlreadyAssignedCloseBtn = styled.button`
  margin-top: 8px;
  width: 100%;
  height: 42px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%);
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(201, 77, 170, 0.25);
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
`;